import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { FiRefreshCw, FiArrowLeft, FiCheckCircle } from 'react-icons/fi';
import { startProCheckout } from '../utils/billing';
import { consumePostLoginPath } from '../services/authService';
import AuthShell, { AuthField } from '../v2/auth/AuthShell';

const Register = () => {
    const [credentials, setCredentials] = useState({
        organization: '',
        firstname: '',
        lastname: '',
        email: '',
        confirmemail: '',
        password: '',
        confirmpassword: '',
        contact_number: ''
    });
    const [emailError, setEmailError] = useState(false);
    const [passwordError, setPasswordError] = useState(false);
    const [verificationCode, setVerificationCode] = useState('');
    const [isCodeSent, setIsCodeSent] = useState(false);
    const [timer, setTimer] = useState(900); // 15 minutes in seconds
    const [error, setError] = useState('');
    const [isCodeExpired, setIsCodeExpired] = useState(false);
    const [step, setStep] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [progress, setProgress] = useState(0);
    // Separate from `step`, which now runs details -> code -> password. The confirmation
    // is an outcome, not a fourth thing to fill in.
    const [done, setDone] = useState(false);
    const navigate = useNavigate();

    // Countdown timer for code expiration
    useEffect(() => {
        let countdown;
        if (isCodeSent && timer > 0) {
            countdown = setInterval(() => {
                setTimer((prevTimer) => {
                    if (prevTimer <= 1) {
                        clearInterval(countdown);
                        setIsCodeExpired(true);
                        return 0;
                    }
                    return prevTimer - 1;
                });
            }, 1000);
        }
        return () => clearInterval(countdown);
    }, [isCodeSent, timer]);

    // Update progress based on form completion
    useEffect(() => {
        if (step === 1) {
            // Only the fields on this step. Counting password and confirmpassword — which
            // are not asked for until step 3 — capped the bar at 75% however complete the
            // form was.
            const stepOne = ['organization', 'firstname', 'lastname', 'email', 'confirmemail', 'contact_number'];
            const filled = stepOne.filter((k) => credentials[k] !== '').length;
            setProgress(Math.min(100, Math.round((filled / stepOne.length) * 100)));
        }
    }, [credentials, step]);

    const handleChange = (e) => {
        setError('');
        setEmailError(false);
        setPasswordError(false);
        const { name, value } = e.target;
        setCredentials({ ...credentials, [name]: value });
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        if (credentials.confirmemail !== credentials.email) {
            setError("Emails don't match.");
            setEmailError(true);
            setIsLoading(false);
            return;
        }
        try {
            await axios.post('s1/api/register', credentials);
            setIsCodeSent(true);
            setStep(2);
        } catch (error) {
            if (error.response) {
                setError(`Registration failed: ${error.response.data.error}`);
            } else {
                setError('Registration failed: Server is down. Try again later.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Step 2 checks the code on its own. /verify-code is a pure read — it does not
    // consume the verification row — so the real /verify call at step 3 still works with
    // the same code. Worth the extra request: otherwise a mistyped digit is only caught
    // after someone has chosen and confirmed a password.
    const handleCodeSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);
        try {
            await axios.post('s1/api/verify-code', {
                email: credentials.email,
                code: verificationCode,
            });
            setStep(3);
        } catch (error) {
            setError(error.response?.data?.error || 'That code was not accepted. Check it and try again.');
        } finally {
            setIsLoading(false);
        }
    };

    // Step 3 is what actually creates the account: /register only stored the email and a
    // code, so until this call succeeds there is no user and no tenant.
    const handleVerify = async (e) => {
        e.preventDefault();
        setError('');

        if (credentials.password.length < 6) {
            setError('Use at least 6 characters.');
            setPasswordError(true);
            return;
        }
        if (credentials.password !== credentials.confirmpassword) {
            setError("Passwords don't match.");
            setPasswordError(true);
            return;
        }

        setIsLoading(true);
        try {
            const response = await axios.post('s1/api/verify', {
                organization: credentials.organization,
                email: credentials.email,
                code: verificationCode,
                firstname: credentials.firstname,
                lastname: credentials.lastname,
                password: credentials.password,
                contact_number: credentials.contact_number,
            });
            localStorage.setItem('token', response.data.token);
            setDone(true);
            
            // If coming from landing page, send them straight into checkout — same path as
            // the "Upgrade to Pro" button elsewhere. If checkout can't start (e.g. Stripe
            // billing is off), carry on to the dashboard rather than stalling here.
            if (isFromLanding) {
                setTimeout(async () => {
                    const redirected = await startProCheckout();
                    if (!redirected) navigate('/dashboard');
                }, 1500);
            } else {
                // Regular flow - redirect to dashboard
                setTimeout(() => navigate(consumePostLoginPath()), 1500);
            }
        } catch (error) {
            setError(error.response?.data?.error || 'Verification failed. Try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendCode = async () => {
        setIsLoading(true);
        try {
            await axios.post('s1/api/resend', { email: credentials.email });
            setIsCodeExpired(false);
            setTimer(900);
            setIsCodeSent(true);
        } catch (error) {
            setError('Failed to resend code. Try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const formatTime = useCallback((seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    }, []);

    const handleBack = () => {
        if (step > 1) {
            setStep(step - 1);
            setError('');
        }
    };

    // Get the query param "from" in the url to check if coming from landing page
    const searchParams = new URLSearchParams(window.location.search);
    const from = searchParams.get("from");
    const isFromLanding = from === "landing";

    // Shown as you type rather than only on submit, which is what someone re-typing an
    // address expects. OR-ed with the submit-time flags so a server-rejected pair stays
    // marked even after the fields agree again.
    const emailMismatch = emailError
        || (credentials.confirmemail !== '' && credentials.email !== credentials.confirmemail);
    const passwordMismatch = passwordError
        || (credentials.confirmpassword !== '' && credentials.password !== credentials.confirmpassword);

    const STEP_LABEL = `Step ${step} of 3`;

    return (
        <AuthShell modules={7}>
            {step === 1 && (
                <>
                    <span className="auth-eyebrow">{STEP_LABEL}</span>
                    <h1 className="auth-title">Create your account</h1>
                    <p className="auth-sub">
                        Set up your organization. You can invite the rest of your team once you're in.
                    </p>

                    <div className="auth-progress"><span style={{ width: `${progress}%` }} /></div>

                    <form onSubmit={handleRegister} className="auth-fields">
                        <div className="auth-grid2">
                            <AuthField label="First name" required>
                                <input className="auth-input" name="firstname" autoComplete="given-name"
                                       value={credentials.firstname} onChange={handleChange} required />
                            </AuthField>
                            <AuthField label="Last name" required>
                                <input className="auth-input" name="lastname" autoComplete="family-name"
                                       value={credentials.lastname} onChange={handleChange} required />
                            </AuthField>
                        </div>

                        <AuthField label="Organization" required>
                            <input className="auth-input" name="organization" autoComplete="organization"
                                   value={credentials.organization} onChange={handleChange} required />
                        </AuthField>

                        <AuthField label="Email" required>
                            <input className="auth-input" name="email" type="email" autoComplete="email"
                                   value={credentials.email} onChange={handleChange} required />
                        </AuthField>

                        <AuthField label="Confirm email" required
                                   error={emailMismatch ? "These email addresses don't match." : ''}>
                            <input className={`auth-input${emailMismatch ? ' bad' : ''}`}
                                   name="confirmemail" type="email" autoComplete="email"
                                   value={credentials.confirmemail} onChange={handleChange} required />
                        </AuthField>

                        <AuthField label="Contact number" required
                                   hint="Any format. International numbers are supported.">
                            <input className="auth-input" name="contact_number" type="tel" autoComplete="tel"
                                   value={credentials.contact_number} onChange={handleChange} required />
                        </AuthField>

                        {error && <div className="auth-note">{error}</div>}

                        <button type="submit" className="auth-btn" disabled={isLoading}>
                            {isLoading ? 'Creating…' : 'Continue'}
                        </button>
                    </form>

                    <p className="auth-foot" style={{ marginTop: '1.5rem' }}>
                        Already have an account? <Link to="/login">Sign in</Link>
                    </p>
                </>
            )}

            {step === 2 && (
                <>
                    <span className="auth-eyebrow">{STEP_LABEL}</span>
                    <h1 className="auth-title">Check your email</h1>
                    <p className="auth-sub">We sent a six-digit code to {credentials.email}.</p>

                    <div className="auth-progress"><span style={{ width: '66%' }} /></div>

                    <form onSubmit={handleCodeSubmit} className="auth-fields">
                        <AuthField label="Verification code" required>
                            <div style={{ display: 'flex', gap: '.5rem' }}>
                                <input className="auth-input" type="text" inputMode="numeric"
                                       autoComplete="one-time-code" placeholder="000000"
                                       value={verificationCode}
                                       onChange={(e) => setVerificationCode(e.target.value)} required />
                                <button type="button" className="auth-btn auth-btn--ghost"
                                        style={{ width: 'auto', flex: 'none', display: 'inline-flex', alignItems: 'center', gap: '.375rem' }}
                                        onClick={handleResendCode} disabled={!isCodeExpired || isLoading}>
                                    <FiRefreshCw /> Resend
                                </button>
                            </div>
                        </AuthField>

                        {isCodeExpired
                            ? <p className="auth-error">That code has expired. Send another.</p>
                            : <p className="auth-help">Expires in {formatTime(timer)}</p>}

                        {error && <div className="auth-note">{error}</div>}

                        <button type="submit" className="auth-btn" disabled={isLoading}>
                            {isLoading ? 'Checking…' : 'Continue'}
                        </button>
                    </form>

                    <p className="auth-foot" style={{ marginTop: '1.5rem' }}>
                        <button type="button" className="auth-linkbtn" onClick={handleBack}>
                            <FiArrowLeft /> Back to your details
                        </button>
                    </p>
                </>
            )}

            {step === 3 && !done && (
                <>
                    <span className="auth-eyebrow">{STEP_LABEL}</span>
                    <h1 className="auth-title">Choose a password</h1>
                    <p className="auth-sub">
                        Use at least 6 characters. You'll sign in with this from now on.
                    </p>

                    <div className="auth-progress"><span style={{ width: '100%' }} /></div>

                    <form onSubmit={handleVerify} className="auth-fields">
                        <AuthField label="Password" required hint="At least 6 characters">
                            <input className="auth-input" name="password" type="password"
                                   autoComplete="new-password" minLength={6}
                                   value={credentials.password} onChange={handleChange} required />
                        </AuthField>

                        <AuthField label="Confirm password" required
                                   error={passwordMismatch ? "These passwords don't match." : ''}>
                            <input className={`auth-input${passwordMismatch ? ' bad' : ''}`}
                                   name="confirmpassword" type="password" autoComplete="new-password"
                                   value={credentials.confirmpassword} onChange={handleChange} required />
                        </AuthField>

                        {error && <div className="auth-note">{error}</div>}

                        <button type="submit" className="auth-btn" disabled={isLoading}>
                            {isLoading ? 'Creating your account…' : 'Create account'}
                        </button>
                    </form>

                    <p className="auth-foot" style={{ marginTop: '1.5rem' }}>
                        <button type="button" className="auth-linkbtn" onClick={handleBack}>
                            <FiArrowLeft /> Back to the code
                        </button>
                    </p>
                </>
            )}

            {done && (
                <div style={{ textAlign: 'center' }}>
                    <div className="nf-mark" style={{ background: 'var(--v2-success-subtle)', color: 'var(--v2-success)' }}>
                        <FiCheckCircle size={26} />
                    </div>
                    <h1 className="auth-title">You're all set</h1>
                    <p className="auth-sub">
                        {isFromLanding
                            ? 'Taking you to checkout…'
                            : 'Taking you to your dashboard…'}
                    </p>
                </div>
            )}
        </AuthShell>
    );
};

export default Register;